import { Label } from "@/components/ui/label"
import { ParamSchema } from "@/lib/form/schema"
import { FormParamStatus, WidgetBaseProps } from "@/lib/form/types"
import { useEffect, useState } from "react"
import { useWatch } from "@/lib/form/form"
import { useFormContext } from "@/lib/form/form-context"
import { format } from "date-fns"
import DatePicker from "@/components/date-picker"

export interface DatePickerWidgetProps extends WidgetBaseProps<ParamSchema> {
  className?: string
}

export default function DatePickerWidget(props: DatePickerWidgetProps) {
  const { schema, className, path } = props
  const { form, readOnly: ctxReadOnly, sheetHasActiveBreakdown } = useFormContext()

  const value = useWatch({
    form: form,
    name: path,
    deep: 1,
  })
  const formatted = useFormatted()

  function useFormatted() {
    const [formatted, setFormatted] = useState("")

    useEffect(() => {
      try {
        if (!value?.value) {
          setFormatted("")

          return
        }

        setFormatted(format(value?.value, "dd-MM-yyyy"))
      } catch {
        setFormatted("")
      }
    }, [value?.value])

    return formatted
  }

  function handlePickDate(date: string) {
    form.setValue(
      path,
      {
        id: schema.id,
        value: date,
        status: FormParamStatus.DRAFT,
        recordedAt: new Date().toISOString(),
      },
      {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      }
    )
  }

  function getPreviewValue() {
    if (value?.status === FormParamStatus.NOT_APPLICABLE) {
      return "NA"
    }

    if (!formatted && sheetHasActiveBreakdown) {
      return "NA"
    }

    return formatted || "PENDING"
  }

  if (value?.status && value?.status !== FormParamStatus.DRAFT) {
    return (
      <div className="flex flex-col gap-2 text-sm">
        <Label>{schema.name}</Label>

        <p className="font-medium">{getPreviewValue()}</p>
      </div>
    )
  }

  return (
    <DatePicker
      className={className}
      label={schema.name}
      value={value?.value}
      disabled={!!sheetHasActiveBreakdown || !!ctxReadOnly}
      onChange={handlePickDate}
    />
  )
}
